/* eslint-disable max-lines */
import { LoadingSpinner, Text, Title } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import {
  Alert,
  Box,
  Button,
  Divider,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  ThemeProvider,
} from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { logEvent } from "@src/appV2/lib/analytics";
import { getAppInfo } from "@src/appV2/lib/utils/capacitor/deviceUtils";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { USER_EVENTS } from "@src/constants";
import { Deploy } from "cordova-plugin-ionic";
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react";

import { configureDeploy } from "../../../app/useOtaUpdate/utils";
import { isCapacitorPlatform, useToast } from "../../lib";

enum OtaUpdateState {
  IDLE = "idle",
  CHECKING = "checking",
  DOWNLOADING = "downloading",
  EXTRACTING = "extracting",
  READY_TO_RELOAD = "ready_to_reload",
  UPDATE_AVAILABLE = "update_available",
  NO_UPDATE_AVAILABLE = "no_update_available",
  ERROR = "error",
}

interface UpdateInfo {
  available: boolean;
  build?: string;
  snapshot?: string;
  channel?: string;
  binaryVersionName?: string;
  binaryVersionCode?: string;
}

export function UpdatesPage(): ReactElement {
  const { showErrorToast, showSuccessToast } = useToast();
  const [currentVersion, setCurrentVersion] = useState<string>("");
  const [currentOtaBuildId, setCurrentOtaBuildId] = useState<string>("");
  const [updateState, setUpdateState] = useState<OtaUpdateState>(OtaUpdateState.IDLE);
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | undefined>();
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isLoadingCurrentVersion, setIsLoadingCurrentVersion] = useState<boolean>(true);

  const scrollRef = useRef<HTMLDivElement>(null);
  const isCapacitor = isCapacitorPlatform();

  // Load current version info on mount
  useEffect(() => {
    const loadCurrentVersionInfo = async () => {
      if (!isCapacitor) {
        setIsLoadingCurrentVersion(false);
        return;
      }

      try {
        const appInfo = await getAppInfo();
        setCurrentVersion(appInfo.version);
        setCurrentOtaBuildId(appInfo.otaBuildId || "Not available");
      } catch (error) {
        showErrorToast("Failed to load app version info");
        setErrorMessage(`Failed to load version: ${String(error)}`);
      } finally {
        setIsLoadingCurrentVersion(false);
      }
    };

    void loadCurrentVersionInfo();
  }, [isCapacitor, showErrorToast]);

  const checkForUpdate = useCallback(async () => {
    if (!isCapacitor) {
      showErrorToast("OTA updates are only available on mobile devices");
      return;
    }

    setUpdateState(OtaUpdateState.CHECKING);
    setErrorMessage("");
    setDownloadProgress(0);

    try {
      await configureDeploy();
      const info = await Deploy.checkForUpdate();

      logEvent(USER_EVENTS.TAPPED_ACCOUNT_MENU_ITEM, {
        menuItemName: "Check for Updates",
        updateAvailable: info.available,
      });

      if (info.available) {
        setUpdateInfo(info);
        setUpdateState(OtaUpdateState.UPDATE_AVAILABLE);
        showSuccessToast("New update available!");
      } else {
        setUpdateInfo(info);
        setUpdateState(OtaUpdateState.NO_UPDATE_AVAILABLE);
        showSuccessToast("You're on the latest version");
      }
    } catch (error) {
      setUpdateState(OtaUpdateState.ERROR);
      setErrorMessage(`Check failed: ${String(error)}`);
      showErrorToast("Failed to check for updates");
      logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOAD_ERROR, { error: String(error) });
    }
  }, [isCapacitor, showErrorToast, showSuccessToast]);

  const downloadAndExtractUpdate = useCallback(async () => {
    if (!isCapacitor || !updateInfo?.build) {
      showErrorToast("No update available to download");
      return;
    }

    setUpdateState(OtaUpdateState.DOWNLOADING);
    setErrorMessage("");
    setDownloadProgress(0);

    try {
      logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOAD_STARTED, {
        buildId: updateInfo.build,
      });

      const startTime = new Date();

      // Download the update with progress tracking
      await Deploy.downloadUpdate((progress) => {
        setDownloadProgress(progress ?? 0);
      });

      const extractionStartTime = new Date();
      setUpdateState(OtaUpdateState.EXTRACTING);

      // Extract the update
      await Deploy.extractUpdate();

      const endTime = new Date();
      const downloadOnlyTimeInSeconds =
        (extractionStartTime.getTime() - startTime.getTime()) / 1000;
      const extractionTimeInSeconds = (endTime.getTime() - extractionStartTime.getTime()) / 1000;
      const totalTimeInSeconds = (endTime.getTime() - startTime.getTime()) / 1000;

      logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOADED, {
        buildId: updateInfo.build,
        downloadTimeSeconds: totalTimeInSeconds,
        downloadOnlyTimeInSeconds,
        extractionTimeInSeconds,
        totalTimeInSeconds,
      });

      setUpdateState(OtaUpdateState.READY_TO_RELOAD);
      showSuccessToast("Update downloaded successfully!");
    } catch (error) {
      setUpdateState(OtaUpdateState.ERROR);
      setErrorMessage(`Download failed: ${String(error)}`);
      showErrorToast("Failed to download update");
      logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOAD_ERROR, { error: String(error) });
    }
  }, [isCapacitor, updateInfo, showErrorToast, showSuccessToast]);

  const reloadApp = useCallback(async () => {
    if (!isCapacitor) {
      showErrorToast("App reload is only available on mobile devices");
      return;
    }

    try {
      // Reload the app to apply the update
      await Deploy.reloadApp();
    } catch (error) {
      setErrorMessage(`Reload failed: ${String(error)}`);
      showErrorToast("Failed to reload app. Please restart the app manually.");
    }
  }, [isCapacitor, showErrorToast]);

  const renderCurrentVersionInfo = () => {
    if (!isCapacitor) {
      return (
        <Alert severity="info">
          OTA updates are only available on mobile devices (iOS/Android)
        </Alert>
      );
    }

    return (
      <Table>
        <TableBody>
          <TableRow>
            <TableCell sx={{ py: 2, border: 0 }}>
              <Text semibold variant="body2">
                App Version
              </Text>
            </TableCell>
            <TableCell sx={{ py: 2, border: 0 }}>
              <Text variant="body2">{currentVersion || "Unknown"}</Text>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ py: 2, border: 0 }}>
              <Text semibold variant="body2">
                Current OTA Build ID
              </Text>
            </TableCell>
            <TableCell sx={{ py: 2, border: 0 }}>
              <Text variant="body2" sx={{ wordBreak: "break-all" }}>
                {currentOtaBuildId}
              </Text>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  const renderUpdateInfo = () => {
    if (!updateInfo) {
      return null;
    }

    return (
      <Table>
        <TableBody>
          {updateInfo.available && updateInfo.build && (
            <TableRow>
              <TableCell sx={{ py: 2, border: 0 }}>
                <Text semibold variant="body2">
                  Available Build ID
                </Text>
              </TableCell>
              <TableCell sx={{ py: 2, border: 0 }}>
                <Text variant="body2" sx={{ wordBreak: "break-all" }}>
                  {updateInfo.build}
                </Text>
              </TableCell>
            </TableRow>
          )}
          {updateInfo.snapshot && (
            <TableRow>
              <TableCell sx={{ py: 2, border: 0 }}>
                <Text semibold variant="body2">
                  Snapshot
                </Text>
              </TableCell>
              <TableCell sx={{ py: 2, border: 0 }}>
                <Text variant="body2" sx={{ wordBreak: "break-all" }}>
                  {updateInfo.snapshot}
                </Text>
              </TableCell>
            </TableRow>
          )}
          {updateInfo.channel && (
            <TableRow>
              <TableCell sx={{ py: 2, border: 0 }}>
                <Text semibold variant="body2">
                  Channel
                </Text>
              </TableCell>
              <TableCell sx={{ py: 2, border: 0 }}>
                <Text variant="body2">{updateInfo.channel}</Text>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    );
  };

  const renderStatusMessage = () => {
    if (updateState === OtaUpdateState.CHECKING) {
      return (
        <Stack spacing={3} alignItems="center" justifyContent="center" py={6}>
          <LoadingSpinner size="medium" variant="primary" />
          <Text semibold variant="body1">
            Checking for updates...
          </Text>
        </Stack>
      );
    }

    if (updateState === OtaUpdateState.DOWNLOADING) {
      return (
        <Stack spacing={3} alignItems="center" justifyContent="center" py={6}>
          <LoadingSpinner size="medium" variant="primary" />
          <Stack spacing={2} alignItems="center" width="100%">
            <Text semibold variant="body1">
              Downloading update... {downloadProgress}%
            </Text>
            <Box sx={{ width: "80%", maxWidth: 300 }}>
              <LinearProgress variant="determinate" value={downloadProgress} />
            </Box>
          </Stack>
        </Stack>
      );
    }

    if (updateState === OtaUpdateState.EXTRACTING) {
      return (
        <Stack spacing={3} alignItems="center" justifyContent="center" py={6}>
          <LoadingSpinner size="medium" variant="primary" />
          <Stack spacing={2} alignItems="center" width="100%">
            <Text semibold variant="body1">
              Extracting update...
            </Text>
            <Box sx={{ width: "80%", maxWidth: 300 }}>
              <LinearProgress />
            </Box>
          </Stack>
        </Stack>
      );
    }

    if (updateState === OtaUpdateState.READY_TO_RELOAD) {
      return (
        <Alert severity="success">
          <Text semibold variant="body2">
            Update downloaded and ready! Reload the app to apply the update.
          </Text>
        </Alert>
      );
    }

    if (updateState === OtaUpdateState.UPDATE_AVAILABLE) {
      return (
        <Alert severity="success">
          <Text semibold variant="body2">
            A new update is available!
          </Text>
        </Alert>
      );
    }

    if (updateState === OtaUpdateState.NO_UPDATE_AVAILABLE) {
      return (
        <Alert severity="info">
          <Text semibold variant="body2">
            You&apos;re on the latest version
          </Text>
        </Alert>
      );
    }

    if (updateState === OtaUpdateState.ERROR) {
      return (
        <Alert severity="error">
          <Text semibold variant="body2">
            {errorMessage || "An error occurred"}
          </Text>
        </Alert>
      );
    }

    return null;
  };

  const renderActionButtons = () => {
    const isCheckingOrDownloading =
      updateState === OtaUpdateState.CHECKING ||
      updateState === OtaUpdateState.DOWNLOADING ||
      updateState === OtaUpdateState.EXTRACTING;

    return (
      <Stack spacing={2}>
        <Button
          fullWidth
          variant="contained"
          disabled={!isCapacitor || isCheckingOrDownloading}
          onClick={checkForUpdate}
        >
          Check for Updates
        </Button>

        {updateState === OtaUpdateState.UPDATE_AVAILABLE && (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={!isCapacitor || isCheckingOrDownloading}
            onClick={downloadAndExtractUpdate}
          >
            Download Update
          </Button>
        )}

        {updateState === OtaUpdateState.READY_TO_RELOAD && (
          <Button fullWidth variant="contained" color="success" onClick={reloadApp}>
            Reload App Now
          </Button>
        )}
      </Stack>
    );
  };

  // Show full-page loading spinner while loading initial version info
  if (isLoadingCurrentVersion && isCapacitor) {
    return (
      <ThemeProvider theme={getTheme()}>
        <PageWrapper variant="tertiary">
          <StickyScrollPageHeader
            title="Updates"
            scrollContainerRef={scrollRef}
            headerVariant="tertiary"
            fallbackGoBackPath={`${RootPaths.HOME}/account`}
          />
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}
          >
            <Stack spacing={3} alignItems="center">
              <LoadingSpinner size="large" variant="primary" />
              <Text semibold variant="body1" color={(theme) => theme.palette.text.secondary}>
                Loading version info...
              </Text>
            </Stack>
          </Box>
        </PageWrapper>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Updates"
          scrollContainerRef={scrollRef}
          headerVariant="tertiary"
          fallbackGoBackPath={`${RootPaths.HOME}/account`}
        />

        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            paddingBottom: 4,
          }}
        >
          <Stack spacing={4} padding={3} paddingTop={2}>
            <Stack spacing={3}>
              <Title component="h4" textAlign="center">
                Current Version
              </Title>
              {renderCurrentVersionInfo()}
            </Stack>

            <Divider />

            {renderStatusMessage() && (
              <>
                <Box>{renderStatusMessage()}</Box>
                <Divider />
              </>
            )}

            {updateInfo && (
              <>
                <Stack spacing={3}>
                  <Title component="h4" textAlign="center">
                    Update Information
                  </Title>
                  {renderUpdateInfo()}
                </Stack>
                <Divider />
              </>
            )}

            <Stack spacing={3}>
              <Title component="h4" textAlign="center">
                Actions
              </Title>
              {renderActionButtons()}
            </Stack>

            <Alert severity="info">
              <Stack spacing={1.5}>
                <Text semibold variant="body2">
                  About OTA Updates:
                </Text>
                <Text variant="body2">
                  • OTA (Over-The-Air) updates allow the app to update without requiring an App
                  Store update
                </Text>
                <Text variant="body2">
                  • Updates are normally downloaded automatically in the background
                </Text>
                <Text variant="body2">
                  • After download, updates are applied when you restart the app
                </Text>
                <Text variant="body2">
                  • Use this page to manually check for and install updates immediately
                </Text>
              </Stack>
            </Alert>
          </Stack>
        </Box>
      </PageWrapper>
    </ThemeProvider>
  );
}
/* eslint-enable max-lines */
